import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { loadUser } from '../../features/auth/authSlice';
import type { ParishAdminOnboardingPhase } from '../../types/types';
import { normalizeWithdrawalPanelMode } from '../../utils/parishWithdrawalPanel';
import { useBrandRegistrationCopy } from '../../brands/registrationCopy';

const PHASE_TITLE_PT: Record<string, string> = {
  'onboarding.phase.confirm_email': 'Confirmar seu e-mail',
  'onboarding.phase.password_access': 'Senha e acesso ao painel',
  'onboarding.phase.entity_data': 'Dados da sua entidade (paróquia)',
  'onboarding.phase.receiving_account': 'Conta para receber na paróquia',
  'onboarding.phase.payments_ready': 'Pagamentos com Pix prontos',
};

const PHASE_BLURB_PT: Record<string, string> = {
  'onboarding.phase.confirm_email':
    'Se você já entrou no painel com sua senha, o e-mail desta conta é considerado confirmado automaticamente. Contas novas pelo convite confirmam ao definir a senha no link do e-mail.',
  'onboarding.phase.password_access':
    'Você já entrou no painel. Guarde bem sua senha e não compartilhe com outras pessoas.',
  'onboarding.phase.entity_data':
    'Complete nome, telefone de contato, endereço e CNPJ/CPF institucional no perfil para termos dados corretos em contratos e notas quando necessário.',
  'onboarding.phase.receiving_account':
    'Cadastre a conta para onde entram valores de inscrições: dados da pessoa física ou jurídica e conta bancária. Na página Saque você envia para a operadora e acompanha o status.',
  'onboarding.phase.payments_ready':
    'Quando a conta para receber estiver aprovada e o Pix estiver disponível aqui para sua paróquia, o valor das inscrições pode ser cobrado com segurança, com divisão automática entre paróquia e plataforma (taxa combinada no cadastro).',
};

function PhaseAction(props: {
  phase: ParishAdminOnboardingPhase;
  withdrawalPanelHidden: boolean;
  onRefreshUser: () => void;
  refreshing: boolean;
}) {
  const { phase, withdrawalPanelHidden, onRefreshUser, refreshing } = props;
  const t = useBrandRegistrationCopy();

  switch (phase.id) {
    case 'confirm_email':
      if (phase.done) return null;
      return (
        <Button size="small" variant="outlined" onClick={onRefreshUser} disabled={refreshing}>
          {refreshing ? 'Atualizando…' : 'Atualizar status do e-mail'}
        </Button>
      );
    case 'entity_data':
      return (
        <Button component={RouterLink} to="/dashboard/perfil" size="small" variant="contained">
          {t('Abrir perfil da paróquia')}
        </Button>
      );
    case 'receiving_account':
    case 'payments_ready':
      return withdrawalPanelHidden ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ maxWidth: 360 }}>
          {t('O módulo de saque está oculto para esta paróquia. Quando a plataforma liberar, use a aba Saque no painel do evento.')}
        </Typography>
      ) : (
        <Button component={RouterLink} to="/dashboard/saque" size="small" variant="contained" color={phase.done ? 'primary' : 'secondary'}>
          Abrir conta para receber e Pix / Saque
        </Button>
      );
    default:
      return null;
  }
}

const ParishAdminOnboardingPage: React.FC = () => {
  const t = useBrandRegistrationCopy();
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((s: RootState) => s.auth.user);
  const authLoading = useSelector((s: RootState) => s.auth.loading);
  const onboarding = user?.parishAdminOnboarding;
  const navigate = useNavigate();
  const withdrawalPanelHidden =
    normalizeWithdrawalPanelMode(user?.parish?.features?.withdrawalPanelMode) === 'hidden';

  React.useEffect(() => {
    if (user?.userType === 'parish_admin' && !onboarding?.phases?.length) {
      void dispatch(loadUser());
    }
  }, [dispatch, user?.userType, onboarding?.phases?.length]);

  if (!user || user.userType !== 'parish_admin') {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Typography>{t('Área apenas para administradores de paróquia.')}</Typography>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/dashboard')}>
          Voltar
        </Button>
      </Container>
    );
  }

  if (!onboarding?.phases?.length) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Stack spacing={2} alignItems="center">
          {authLoading ? <Typography>Carregando seu progresso…</Typography> : null}
          {!authLoading ? (
            <>
              <Typography color="text.secondary" textAlign="center">
                Não foi possível carregar os passos do cadastro. Tente atualizar ou volte ao painel.
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={() => dispatch(loadUser())}>
                  Tentar de novo
                </Button>
                <Button component={RouterLink} to="/dashboard">
                  Voltar ao painel
                </Button>
              </Stack>
            </>
          ) : null}
        </Stack>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Stack spacing={2}>
        <Typography variant="h5" component="h1" fontWeight={600}>
          Concluir seu cadastro
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Passo a passo em linguagem simples. Você só precisa das etapas que ainda estiverem pendentes.
        </Typography>

        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
            <Typography variant="subtitle2">
              Progresso{' '}
              {onboarding.phases.filter((p: ParishAdminOnboardingPhase) => p.done).length}/{onboarding.phases.length}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {onboarding.progressPercent}% concluído
            </Typography>
          </Stack>
          <LinearProgress variant="determinate" value={onboarding.progressPercent} sx={{ borderRadius: 1, height: 8 }} />
        </Box>

        <Typography variant="body2">
          Glossário rápido: dizemos{' '}
          <strong>{t('conta para receber na paróquia')}</strong> em vez de termos de sistema;&nbsp;
          <strong>divisão automática com a plataforma</strong> {t('refere-se ao repasse combinado quando alguém paga uma inscrição.')}
        </Typography>

        <List disablePadding sx={{ gap: 1.5, display: 'flex', flexDirection: 'column' }}>
          {onboarding.phases.map((phase: ParishAdminOnboardingPhase) => (
            <Card key={phase.id} variant="outlined" sx={{ bgcolor: phase.done ? 'action.hover' : 'background.paper' }}>
              <CardContent sx={{ '&:last-child': { pb: 2 }, py: 2 }}>
                <ListItem sx={{ px: 0, alignItems: 'flex-start' }}>
                  <ListItemIcon sx={{ minWidth: 40, mt: 0.25 }}>
                    {phase.done ? (
                      <CheckCircleRoundedIcon color="success" />
                    ) : (
                      <RadioButtonUncheckedRoundedIcon color="disabled" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography fontWeight={600}>
                        {t(PHASE_TITLE_PT[phase.titleKey] ?? phase.titleKey)}
                      </Typography>
                    }
                    secondary={
                      <Box component="span" sx={{ display: 'block', mt: 0.75 }}>
                        <Typography variant="body2" color="text.secondary" component="span" display="block">
                          {t(PHASE_BLURB_PT[phase.titleKey] ?? '')}
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }}>
                          <PhaseAction
                            phase={phase}
                            withdrawalPanelHidden={withdrawalPanelHidden}
                            onRefreshUser={() => dispatch(loadUser())}
                            refreshing={authLoading}
                          />
                        </Stack>
                      </Box>
                    }
                  />
                </ListItem>
              </CardContent>
            </Card>
          ))}
        </List>

        {!onboarding.emailVerified ? (
          <Typography variant="caption" color="text.secondary" display="block">
            Se você já está logado com senha, clique em «Atualizar status do e-mail» acima ou recarregue a página. Enquanto o e-mail não constar confirmado, eventos cobrados, conta para receber e saques permanecem bloqueados.
          </Typography>
        ) : null}

        <Button component={RouterLink} to="/dashboard" variant="text">
          Voltar ao painel
        </Button>
      </Stack>
    </Container>
  );
};

export default ParishAdminOnboardingPage;
